import pdfMake from 'pdfmake/build/pdfmake';
import { vfs as pdfFontsVfs } from 'pdfmake/build/vfs_fonts';

(pdfMake as any).vfs = pdfFontsVfs;

export interface StockCountLine {
  productId: string;
  code: string;
  name: string;
  unit: string;
  barcode?: string;
  expected: number;
  unitCost?: number;
}

export interface StockCountDoc {
  id: string;
  number: string;
  warehouseName: string;
  status: 'open' | 'closed' | 'cancelled';
  createdAt?: Date;
  createdByName?: string;
  closedAt?: Date;
  closedByName?: string;
  note?: string;
  lines: StockCountLine[];
  counts: Record<string, number>;
}

const fmt = (n: number) => new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 3 }).format(n);
const money = (n: number) => new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(n || 0);
const d = (x?: Date) => (x ? x.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

/**
 * Leltár PDF.
 * blank = true: üres leltárív (kézi számoláshoz, a „Számolt” oszlop üres, elvárt mennyiség opcionálisan rejtve – vak leltár)
 * blank = false: leltár-jegyzőkönyv eltérésekkel és értékkülönbözettel, aláírási sorokkal.
 */
export function generateStockCountPdf(sc: StockCountDoc, opts: { blank?: boolean; blind?: boolean; orgName?: string } = {}) {
  const { blank = false, blind = false, orgName = '' } = opts;
  const lines = sc.lines.slice().sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  const head = blank
    ? ['#', 'Cikkszám', 'Megnevezés', 'Egység', ...(blind ? [] : ['Nyilv.']), 'Számolt', 'Megjegyzés']
    : ['#', 'Cikkszám', 'Megnevezés', 'Egység', 'Nyilv.', 'Számolt', 'Eltérés', 'Érték eltérés'];
  let plus = 0, minus = 0, diffCount = 0, uncounted = 0;
  const body: any[] = [head.map(h => ({ text: h, style: 'th' }))];
  lines.forEach((l, i) => {
    const counted = sc.counts[l.productId];
    if (blank) {
      body.push([String(i + 1), l.code, l.name, l.unit, ...(blind ? [] : [{ text: fmt(l.expected), alignment: 'right' }]), '', '']);
      return;
    }
    const has = counted != null;
    if (!has) uncounted++;
    const diff = has ? counted - l.expected : 0;
    const vdiff = diff * (l.unitCost || 0);
    if (diff !== 0) { diffCount++; if (vdiff > 0) plus += vdiff; else minus += vdiff; }
    body.push([
      String(i + 1), l.code, l.name, l.unit,
      { text: fmt(l.expected), alignment: 'right' },
      { text: has ? fmt(counted) : '–', alignment: 'right', color: has ? undefined : '#9ca3af' },
      { text: has ? (diff > 0 ? '+' : '') + fmt(diff) : '', alignment: 'right', color: diff > 0 ? '#047857' : diff < 0 ? '#be123c' : undefined, bold: diff !== 0 },
      { text: has && diff !== 0 && l.unitCost ? money(vdiff) : '', alignment: 'right', color: vdiff > 0 ? '#047857' : vdiff < 0 ? '#be123c' : undefined }
    ]);
  });

  const widths = blank ? (blind ? [18, 60, '*', 34, 55, 90] : [18, 60, '*', 34, 45, 55, 80]) : [18, 55, '*', 32, 45, 45, 45, 65];
  const content: any[] = [
    { text: blank ? 'LELTÁRÍV' : 'LELTÁR-JEGYZŐKÖNYV', style: 'title' },
    { columns: [
      { width: '*', stack: [
        { text: orgName, bold: true },
        { text: `Raktár: ${sc.warehouseName}` },
        { text: `Leltár száma: ${sc.number}` }
      ] },
      { width: 'auto', alignment: 'right', stack: [
        { text: `Megkezdve: ${d(sc.createdAt)}` },
        ...(sc.closedAt ? [{ text: `Lezárva: ${d(sc.closedAt)}` }] : []),
        { text: `Tételek: ${lines.length}` }
      ] }
    ], margin: [0, 4, 0, 10], fontSize: 9 },
    { table: { headerRows: 1, widths, body }, layout: { hLineColor: () => '#e5e7eb', vLineColor: () => '#e5e7eb', fillColor: (r: number) => (r === 0 ? '#f3f4f6' : null), paddingTop: () => (blank ? 6 : 3), paddingBottom: () => (blank ? 6 : 3) }, fontSize: 8 }
  ];
  if (!blank) {
    content.push({ margin: [0, 12, 0, 0], table: { widths: ['*', 'auto'], body: [
      ['Eltérő tételek', String(diffCount)],
      ['Nem számolt tételek (változatlanul maradtak)', String(uncounted)],
      ['Többlet értéke', { text: money(plus), color: '#047857' }],
      ['Hiány értéke', { text: money(minus), color: '#be123c' }],
      [{ text: 'Nettó eltérés', bold: true }, { text: money(plus + minus), bold: true }]
    ] }, layout: 'noBorders', fontSize: 9 });
    if (sc.note) content.push({ text: `Megjegyzés: ${sc.note}`, fontSize: 9, margin: [0, 8, 0, 0] });
  }
  content.push({ margin: [0, 36, 0, 0], columns: [
    { width: '*', stack: [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 170, y2: 0, lineWidth: 0.5 }] }, { text: blank ? 'Számolta' : `Leltározó${sc.createdByName ? ` (${sc.createdByName})` : ''}`, fontSize: 8, margin: [0, 3, 0, 0] }] },
    { width: '*', stack: [{ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 170, y2: 0, lineWidth: 0.5 }] }, { text: blank ? 'Ellenőrizte' : `Jóváhagyó${sc.closedByName ? ` (${sc.closedByName})` : ''}`, fontSize: 8, margin: [0, 3, 0, 0] }] }
  ] });

  const docDef: any = {
    pageSize: 'A4', pageMargins: [32, 32, 32, 40],
    content,
    styles: { title: { fontSize: 15, bold: true, margin: [0, 0, 0, 2] }, th: { bold: true, fontSize: 8, color: '#374151' } },
    defaultStyle: { fontSize: 9 },
    footer: (page: number, pages: number) => ({ text: `${sc.number} · ${page}/${pages}`, alignment: 'center', fontSize: 7, color: '#9ca3af', margin: [0, 12, 0, 0] })
  };
  pdfMake.createPdf(docDef).download(`${blank ? 'leltariv' : 'leltar'}_${sc.number}.pdf`);
}
